import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
// TASK-73 Phase 0: date-fns for the placer button's bookings-window
// filter + Stockholm-tz date derivation from bookings.start_time.
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrgHall } from "@/hooks/useOrgHall";
import { useOrganization } from "@/hooks/useOrganization";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
// UPPDRAG-42: Kategori-fältet bytte från fritext-Input + datalist till
// 3 multi-select checkboxes (Herr / Dam / Mix).
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/ui/number-input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
// UPPDRAG-40: Textarea no longer used here — rules_text + rules_text_en
// switched to RichTextEditor below. Import removed.
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Check, Loader2, Settings2, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import AdminDivisions from "./AdminDivisions";
import LeagueRoundsManager from "./LeagueRoundsManager";
import LeagueCSVImport from "./LeagueCSVImport";
import LeagueCSVExport from "./LeagueCSVExport";
import LeagueCalendar from "./LeagueCalendar";
import LeagueExtraSlots from "./LeagueExtraSlots";
// UPPDRAG-38: per-(league × membership_plan) pricing matrix replaces the
// binary leagues.members_play_free toggle. The toggle is left in place
// for backward compat (a separate UPPDRAG will deprecate it once all
// callers read from league_membership_pricing).
import LeagueMembershipPricing from "./LeagueMembershipPricing";
// UPPDRAG-40: rich text editor for league rules (sv + en). Storage stays
// in the existing TEXT columns; sanitization happens at READ time via
// RichTextDisplay's DOMPurify pass.
import { RichTextEditor } from "@/components/ui/rich-text-editor";
// UPPDRAG-53: date picker with ISO week numbers + Swedish holidays in red.
// Replaces the native <Input type="date"> on the 3 league date fields.
import { DatePickerWithHolidays } from "@/components/ui/date-picker-with-holidays";
import LeagueDivisionTimes from "./LeagueDivisionTimes";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { parseLeagueSaveError } from "@/lib/parseLeagueSaveError";
import SponsorLogoUpload from "@/components/admin/SponsorLogoUpload";

const leagueSchema = z.object({
  name: z.string().min(1).max(100),
  // UPPDRAG-42: legacy free-form text field, kept for backward-compat
  // until all callers migrate to `categories` (text[]). Default value
  // derived from `categories` on save below.
  category: z.string().min(1),
  // UPPDRAG-42: multi-select category list. At least one must be picked.
  categories: z.array(z.enum(["herr", "dam", "mix"])).min(1, "Välj minst en kategori"),
  // PR-10.1: optional FK to public.seasons. Empty string from the
  // "Ingen säsong" Select option is mapped to null at payload time.
  season_id: z.string().nullable().optional(),
  registration_deadline: z.string().optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  match_duration_minutes: z.number().min(30).max(180),
  play_schedule: z.record(z.array(z.object({ start: z.string(), end: z.string() }))),
  cost_per_player: z.number().min(0),
  members_play_free: z.boolean(),
  // PR-12.3: roster size range — inclusive of player1. CHECK constraint
  // on the leagues table also enforces 1 ≤ min ≤ max ≤ 20.
  min_players_per_team: z.number().int().min(1).max(20),
  max_players_per_team: z.number().int().min(1).max(20),
  // TASK-67 PR-D: per-team caps for the two role buckets used by
  // team_members. Backed by leagues.max_primary_per_team /
  // max_reserves_per_team (PR-A added, default 2/2). Range 1..16 for
  // primaries (must have at least one playing slot) and 0..16 for
  // reserves (a league may opt out of reserves entirely).
  max_primary_per_team: z.number().int().min(1).max(16),
  max_reserves_per_team: z.number().int().min(0).max(16),
  sets: z.number().min(1).max(5),
  point_type: z.string().min(1),
  play_all_sets: z.boolean(),
  tiebreak_at: z.string(),
  time_limit_applies: z.boolean(),
  rules_text: z.string().optional(),
  // UPPDRAG-40: English variant of rules — schema column already exists
  // (leagues.rules_text_en) but was only edited via SettingsLeagues. Surfacing
  // it on the league form as a second rich-text field so admins can localize
  // alongside the Swedish version.
  rules_text_en: z.string().optional(),
  // PR-16.1: optional sponsor URLs. Two variants — Mina serier + Anmäl
  // dig — each has a logo URL + click-through URL. Persisted via
  // saveMutation. Values stay as plain strings here; the file-upload
  // UX in PR-16.3 will replace the Input with a file picker that
  // produces a URL.
  sponsor_my_series_logo_url: z.string().optional(),
  sponsor_my_series_url: z.string().optional(),
  sponsor_register_logo_url: z.string().optional(),
  sponsor_register_url: z.string().optional(),
});

type LeagueFormValues = z.infer<typeof leagueSchema>;

const scoringSchema = z.object({
  max_teams_per_division: z.number().min(2).max(20),
  promotion_count: z.number().min(0).max(5),
  relegation_count: z.number().min(0).max(5),
  set_win: z.number(),
  set_loss: z.number(),
  set_draw: z.number(),
  walkover_winner: z.number(),
  walkover_loser: z.number(),
  walkover_no_notice_penalty: z.number(),
  max_wo_before_block: z.number().min(1).max(10),
  // UPPDRAG-39: configurable incomplete-set leader-rule. Threshold is the
  // game-lead at time-out for the set to count as a SET WIN (uses set_win
  // / set_loss); below threshold the slight-leader/trailer get the
  // share_winner/share_loser values respectively. Defaults 2/1/1 = real
  // padel rule.
  incomplete_set_lead_threshold: z.number().min(1).max(7),
  incomplete_set_share_winner: z.number().min(0).max(10),
  incomplete_set_share_loser: z.number().min(0).max(10),
  // UPPDRAG-50: tiebreaker principle for the standings table. Currently
  // INERT — saved to leagues.table_sort_method but not yet read by any
  // sort fn (the standings table is empty in PROD; UPPDRAG-50-followup
  // will build the calc pipeline that reads this column). Allowed values
  // mirror the DB CHECK constraint added in migration 20260502160000.
  table_sort_method: z.enum([
    "standard",
    "head_to_head",
    "game_diff",
    "set_diff",
    "win_percentage",
    "wo_adjusted",
  ]),
  // UPPDRAG-51: ball-holder rotation principle. Read by AdminLeagues
  // publishMutation when generating league_matches — picks which player
  // brings balls to each match per the chosen rule. CHECK enum mirrors
  // the DB constraint added in migration 20260502170000.
  ball_holder_principle: z.enum([
    "alternating_pair",
    "by_match_number",
    "club_provides",
  ]),
  // UPPDRAG-56: hours-before-match for the reminder email. NULL = disabled
  // (admin opt-in per league). Cron 'send-league-match-reminders' picks
  // up matches in window every 30 min. CHECK enum mirrors migration
  // 20260503100000 (1..720h).
  reminder_hours_before: z.number().int().min(1).max(720).nullable(),
});

type ScoringFormValues = z.infer<typeof scoringSchema>;

interface LeagueFormProps {
  league?: any;
  onSuccess: () => void;
}

export default function LeagueForm({ league, onSuccess: _onSuccess }: LeagueFormProps) {
  const { user } = useAuth();
  const { hall, hallHours, courts: orgCourts } = useOrgHall();
  const courts = ((orgCourts ?? []) as any[]).filter((c: any) => c.status === "active").map((c: any) => ({ id: c.id as string, name: c.name as string }));
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  const { t } = useTranslation("admin");
  const [activeTab, setActiveTab] = useState("registration");
  const [autoSaveStatus, setAutoSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const [categoryPrices, setCategoryPrices] = useState<Record<string, number>>({});

  const navigate = useNavigate();
  const [showOverrides, setShowOverrides] = useState(false);
  const [showScoringOverrides, setShowScoringOverrides] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const matchFormat = league?.match_format ?? {};
  const scoringSystem = league?.scoring_system ?? {};

  // Detect if league has overrides vs org defaults
  const hasMatchOverrides = !!(league?.match_format || league?.point_type);
  const hasScoringOverrides = !!(league?.scoring_system?.set_win !== undefined);

  // Fetch customer categories for pricing
  const { data: customerCategories = [] } = useQuery({
    queryKey: ["customer-categories", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("customer_categories")
        .select("id, name, slug, sort_order")
        .eq("organization_id", organization!.id)
        .order("sort_order");
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  // Load existing category prices.
  // Sprint D PR-AB Phase 4-aware: prefer price_minor (öre) over legacy price (kr).
  useEffect(() => {
    if (league?.id) {
      supabase
        .from("activity_category_prices")
        .select("customer_category_id, price, price_minor, currency")
        .eq("league_id", league.id)
        .then(({ data }) => {
          const map: Record<string, number> = {};
          data?.forEach((r: any) => {
            // Prefer Phase 4 _minor → kr; fall back to legacy price for old rows.
            map[r.customer_category_id] = r.price_minor != null
              ? r.price_minor / 100
              : Number(r.price ?? 0);
          });
          setCategoryPrices(map);
        });
    }
  }, [league?.id]);

  // Step 1: Registration form
  const form = useForm<LeagueFormValues>({
    resolver: zodResolver(leagueSchema),
    defaultValues: {
      name: league?.name ?? "",
      // UPPDRAG-42: legacy `category` text mirrors `categories` join.
      // Read prefers existing array; falls back to legacy text via the
      // same backfill rule used in migration 20260501200000.
      category: league?.category ?? "Herr",
      categories: (() => {
        const raw = (league?.categories ?? null) as string[] | null;
        if (Array.isArray(raw) && raw.length > 0) {
          // Coerce only known canonical values (defensive — schema is
          // text[], so anything could in principle land here).
          return raw.filter((c): c is "herr" | "dam" | "mix" =>
            c === "herr" || c === "dam" || c === "mix");
        }
        // Legacy fallback: derive from the free-form text field.
        const t = (league?.category ?? "").toLowerCase();
        const out: ("herr" | "dam" | "mix")[] = [];
        if (t.includes("herr")) out.push("herr");
        if (t.includes("dam")) out.push("dam");
        if (t.includes("mix") || t.includes("öppen") || t.includes("alla")) out.push("mix");
        return out.length > 0 ? out : (["mix"] as ("herr" | "dam" | "mix")[]);
      })(),
      // PR-10.1: prefill season_id when editing an existing league
      season_id: league?.season_id ?? null,
      registration_deadline: league?.registration_deadline
        ? new Date(league.registration_deadline).toISOString().slice(0, 10)
        : "",
      start_date: league?.start_date ? new Date(league.start_date).toISOString().slice(0, 10) : "",
      end_date: league?.end_date ? new Date(league.end_date).toISOString().slice(0, 10) : "",
      match_duration_minutes: league?.match_duration_minutes ?? 90,
      play_schedule: (league?.play_schedule as Record<string, { start: string; end: string }[]>) ?? {},
      cost_per_player: league?.cost_per_player ?? 0,
      members_play_free: league?.members_play_free ?? true,
      // PR-12.3: defaults to 2/2 (current padel-doubles assumption)
      min_players_per_team: league?.min_players_per_team ?? 2,
      max_players_per_team: league?.max_players_per_team ?? 2,
      // TASK-67 PR-D: defaults match PR-A's column DEFAULTs (2/2).
      // Existing leagues that opted into a wider primary roster (e.g. the
      // Vårserien Bears team backfilled to 8 in PR-A) keep their saved
      // value via the `league?.*` fallback.
      max_primary_per_team: league?.max_primary_per_team ?? 2,
      max_reserves_per_team: league?.max_reserves_per_team ?? 2,
      sets: matchFormat.sets ?? 3,
      point_type: league?.point_type ?? matchFormat.point_type ?? "golden_point",
      play_all_sets: matchFormat.play_all_sets ?? true,
      tiebreak_at: matchFormat.tiebreak_at ?? "6-6",
      time_limit_applies: matchFormat.time_limit_applies ?? true,
      rules_text: league?.rules_text ?? "",
      // UPPDRAG-40
      rules_text_en: league?.rules_text_en ?? "",
      // PR-16.1: sponsor URL defaults
      sponsor_my_series_logo_url: league?.sponsor_my_series_logo_url ?? "",
      sponsor_my_series_url: league?.sponsor_my_series_url ?? "",
      sponsor_register_logo_url: league?.sponsor_register_logo_url ?? "",
      sponsor_register_url: league?.sponsor_register_url ?? "",
    },
  });

  // Auto-save (debounced 3s)
  // UPPDRAG-30: NodeJS namespace not declared in browser TS lib.
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerAutoSave = useCallback(() => {
    if (!league?.id) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      const values = form.getValues();
      const match_format = {
        sets: values.sets,
        point_type: values.point_type,
        play_all_sets: values.play_all_sets,
        tiebreak_at: values.tiebreak_at,
        time_limit_applies: values.time_limit_applies,
      };
      setAutoSaveStatus("saving");
      // UPPDRAG-42: derive legacy `category` text from the new array so
      // both columns stay in sync until the legacy column is dropped.
      const legacyCategory = values.categories.length > 0
        ? values.categories.map((c) => c[0].toUpperCase() + c.slice(1)).join("/")
        : (values.category || "Mix");
      supabase.from("leagues").update({
        name: values.name,
        category: legacyCategory,
        categories: values.categories,
        match_duration_minutes: values.match_duration_minutes,
        match_format,
        point_type: values.point_type,
        rules_text: values.rules_text || null,
        // UPPDRAG-40
        rules_text_en: values.rules_text_en || null,
      }).eq("id", league.id).then(({ error }) => {
        if (error) {
          console.warn("Auto-save failed:", error);
          setAutoSaveStatus("idle");
        } else {
          setAutoSaveStatus("saved");
          setTimeout(() => setAutoSaveStatus("idle"), 2000);
        }
      });
    }, 3000);
  }, [form, league?.id]);

  useEffect(() => {
    const subscription = form.watch(() => triggerAutoSave());
    return () => {
      subscription.unsubscribe();
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [form, triggerAutoSave]);

  // Step 2: Scoring form
  const scoringForm = useForm<ScoringFormValues>({
    resolver: zodResolver(scoringSchema),
    defaultValues: {
      max_teams_per_division: league?.max_teams_per_division ?? 8,
      promotion_count: league?.promotion_count ?? 1,
      relegation_count: league?.relegation_count ?? 1,
      set_win: scoringSystem.set_win ?? 3,
      set_loss: scoringSystem.set_loss ?? 0,
      set_draw: scoringSystem.set_draw ?? 1,
      walkover_winner: scoringSystem.walkover_winner ?? 9,
      walkover_loser: scoringSystem.walkover_loser ?? 0,
      walkover_no_notice_penalty: scoringSystem.walkover_no_notice_penalty ?? -2,
      max_wo_before_block: scoringSystem.max_wo_before_block ?? 2,
      // UPPDRAG-39: defaults match the migration column defaults.
      incomplete_set_lead_threshold: league?.incomplete_set_lead_threshold ?? 2,
      incomplete_set_share_winner: league?.incomplete_set_share_winner ?? 1,
      incomplete_set_share_loser: league?.incomplete_set_share_loser ?? 1,
      // UPPDRAG-50: tiebreaker principle. Defaults match the migration's
      // DEFAULT 'standard' (Poäng → gameskillnad → inbördes möte → vinster).
      table_sort_method: (league?.table_sort_method ?? "standard") as
        | "standard" | "head_to_head" | "game_diff" | "set_diff" | "win_percentage" | "wo_adjusted",
      // UPPDRAG-51: ball-holder principle. Defaults match the migration's
      // DEFAULT 'alternating_pair'.
      ball_holder_principle: (league?.ball_holder_principle ?? "alternating_pair") as
        | "alternating_pair" | "by_match_number" | "club_provides",
      // UPPDRAG-56: reminder hours-before. NULL = disabled (default).
      reminder_hours_before: (league?.reminder_hours_before ?? null) as number | null,
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (values: LeagueFormValues) => {
      const match_format = {
        sets: values.sets,
        point_type: values.point_type,
        play_all_sets: values.play_all_sets,
        tiebreak_at: values.tiebreak_at,
        time_limit_applies: values.time_limit_applies,
      };

      // Sprint D PR-AC dual-write: form values are kr, dual-write to _minor (öre).
      // Defensive trigger trg_dual_write_leagues_minor backfills if missing, but app
      // is the source of truth (Sprint E will drop the legacy cols).
      // UPPDRAG-42: same dual-write pattern for the manual save path.
      const legacyCategory = values.categories.length > 0
        ? values.categories.map((c) => c[0].toUpperCase() + c.slice(1)).join("/")
        : (values.category || "Mix");
      const payload: any = {
        name: values.name,
        hall_id: hall!.id,
        category: legacyCategory,
        categories: values.categories,
        registration_deadline: values.registration_deadline || null,
        start_date: values.start_date || null,
        end_date: values.end_date || null,
        match_duration_minutes: values.match_duration_minutes,
        play_schedule: values.play_schedule,
        play_days: Object.keys(values.play_schedule).filter((k) => (values.play_schedule[k]?.length ?? 0) > 0),
        play_time_start: null,
        play_time_end: null,
        cost_per_player: values.cost_per_player,
        cost_per_player_minor: Math.round((values.cost_per_player ?? 0) * 100),
        currency: "SEK",
        members_play_free: values.members_play_free,
        match_format,
        point_type: values.point_type,
        rules_text: values.rules_text || null,
        // UPPDRAG-40
        rules_text_en: values.rules_text_en || null,
        // PR-12.3: persist roster size range
        min_players_per_team: values.min_players_per_team,
        max_players_per_team: values.max_players_per_team,
        // TASK-67 PR-D: persist per-role caps so AdminTeams +
        // CreateTeamDialog cap multi-selects correctly per league.
        max_primary_per_team: values.max_primary_per_team,
        max_reserves_per_team: values.max_reserves_per_team,
        // PR-10.1: persist optional season FK. Empty/undefined → null
        // (matches the "Ingen säsong" Select option).
        season_id: values.season_id || null,
        // PR-16.1: persist sponsor URLs. Empty string → null so the
        // column stays NULL (cleaner queries than empty-string sentinel).
        sponsor_my_series_logo_url: values.sponsor_my_series_logo_url?.trim() || null,
        sponsor_my_series_url: values.sponsor_my_series_url?.trim() || null,
        sponsor_register_logo_url: values.sponsor_register_logo_url?.trim() || null,
        sponsor_register_url: values.sponsor_register_url?.trim() || null,
      };

      // Always update (draft already exists in DB)
      const { error } = await supabase.from("leagues").update(payload).eq("id", league.id);
      if (error) throw error;

      // Upsert category prices
      const { data: membership } = await supabase
        .from("organization_members")
        .select("organization_id")
        .eq("user_id", user!.id)
        .limit(1)
        .single();

      // Sprint D PR-AB dual-write: form values are kr, dual-write to _minor (öre).
      // Defensive trigger trg_dual_write_activity_category_prices_minor backfills if
      // missing, but app is the source of truth (Sprint E will drop legacy cols).
      const priceRows = Object.entries(categoryPrices)
        .filter(([_, price]) => price !== null && price !== undefined)
        .map(([catId, price]) => ({
          organization_id: membership!.organization_id,
          league_id: league.id,
          customer_category_id: catId,
          price,
          price_minor: Math.round(price * 100),
          currency: "SEK",
        }));

      if (priceRows.length > 0) {
        await supabase
          .from("activity_category_prices")
          .upsert(priceRows, { onConflict: "league_id,customer_category_id" });
      }

      return league.id;
    },
    onSuccess: () => {
      toast.success(t("admin:toast.leagueSaved"));
      queryClient.invalidateQueries({ queryKey: ["admin-leagues"] });
    },
    onError: (error: any) => {
      console.error("League save error:", error);
      // PR-16.1: surface the new UNIQUE-constraint violation as a clean
      // Swedish message ("En serie med detta namn finns redan i
      // organisationen.") instead of the raw Postgres detail. Falls
      // through to the existing generic toast for any other error
      // shape.
      const friendly = parseLeagueSaveError(error);
      if (friendly) {
        toast.error(friendly);
      } else {
        toast.error(`${t("leagues.form.error")}: ${error?.message || t("leagues.form.unknownError")}`);
      }
    },
  });

  const saveScoringMutation = useMutation({
    mutationFn: async (values: ScoringFormValues) => {
      const scoring_system = {
        type: "per_set",
        set_win: values.set_win,
        set_loss: values.set_loss,
        set_draw: values.set_draw,
        walkover_winner: values.walkover_winner,
        walkover_loser: values.walkover_loser,
        walkover_no_notice_penalty: values.walkover_no_notice_penalty,
        max_wo_before_block: values.max_wo_before_block,
      };
      // UPPDRAG-39: persist the configurable incomplete-set leader-rule
      // values to the dedicated columns added in migration 20260501100000.
      const { error } = await supabase.from("leagues").update({
        max_teams_per_division: values.max_teams_per_division,
        promotion_count: values.promotion_count,
        relegation_count: values.relegation_count,
        scoring_system,
        incomplete_set_lead_threshold: values.incomplete_set_lead_threshold,
        incomplete_set_share_winner: values.incomplete_set_share_winner,
        incomplete_set_share_loser: values.incomplete_set_share_loser,
        // UPPDRAG-50: persist the tiebreaker principle. CHECK on the column
        // mirrors the zod enum so a typo in either layer fails fast.
        table_sort_method: values.table_sort_method,
        // UPPDRAG-51: persist the ball-holder principle so publishMutation
        // reads the latest value when generating league_matches.
        ball_holder_principle: values.ball_holder_principle,
        // UPPDRAG-56: persist reminder hours-before. NULL disables reminders.
        reminder_hours_before: values.reminder_hours_before,
      }).eq("id", league.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("admin:toast.rulesSaved"));
      queryClient.invalidateQueries({ queryKey: ["admin-leagues"] });
    },
    onError: (error: any) => {
      console.error("Scoring save error:", error);
      toast.error(`${t("leagues.form.error")}: ${error?.message || t("leagues.form.unknownError")}`);
    },
  });

  const hasId = !!league?.id;

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab}>
      <div className="flex items-center justify-between mb-2">
        <TabsList className="w-full">
          <TabsTrigger value="registration" className="flex-1">{t("leagues.form.tabRegistration")}</TabsTrigger>
          <TabsTrigger value="divisions" className="flex-1" disabled={!hasId}>{t("leagues.form.tabDivisions")}</TabsTrigger>
          {/* PR-10.1: rounds tab — disabled until the league has been saved
              (rounds are scoped to a league_id; can't add them before INSERT). */}
          <TabsTrigger value="rounds" className="flex-1" disabled={!hasId}>
            {t("leagues.form.tabRounds", "Omgångar")}
          </TabsTrigger>
          <TabsTrigger value="times" className="flex-1" disabled={!hasId}>{t("leagues.form.tabPlayTimes")}</TabsTrigger>
          <TabsTrigger value="schedule" className="flex-1" disabled={!hasId}>{t("leagues.form.tabSchedule")}</TabsTrigger>
        </TabsList>
      </div>

      {autoSaveStatus !== "idle" && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
          {autoSaveStatus === "saving" && <><Loader2 className="h-3 w-3 animate-spin" /> {t("leagues.form.saving")}</>}
          {autoSaveStatus === "saved" && <><Check className="h-3 w-3 text-green-500" /> {t("leagues.form.saved")}</>}
        </div>
      )}

      {/* Step 1: Registration */}
      <TabsContent value="registration">
        <Form {...form}>
          <form onSubmit={form.handleSubmit((v) => saveMutation.mutate(v))} className="space-y-6">
            <div className="space-y-3">
              <h3 className="font-semibold text-sm">{t("leagues.form.basicInfo")}</h3>
              <FormField control={form.control} name="name" render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("leagues.form.name")}</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              {/* UPPDRAG-42: kategori bytte från fritext + datalist till
                  multi-select checkboxes (Herr / Dam / Mix). Validering
                  via zod kräver minst en bockad. Visuellt: tre checkboxes
                  i rad med klickbara etiketter. Fältet `category` (legacy
                  text) skrivs fortfarande automatiskt vid save så att
                  consumers som inte har migrerat ännu fortsätter funka. */}
              <FormField control={form.control} name="categories" render={({ field }) => {
                const value = (field.value ?? []) as ("herr" | "dam" | "mix")[];
                const toggle = (key: "herr" | "dam" | "mix") => {
                  if (value.includes(key)) {
                    field.onChange(value.filter((v) => v !== key));
                  } else {
                    field.onChange([...value, key]);
                  }
                };
                const items: { key: "herr" | "dam" | "mix"; label: string }[] = [
                  { key: "herr", label: "Herr" },
                  { key: "dam", label: "Dam" },
                  { key: "mix", label: "Mix" },
                ];
                return (
                  <FormItem>
                    <FormLabel>{t("leagues.form.category")}</FormLabel>
                    <FormControl>
                      <div className="flex flex-wrap items-center gap-4">
                        {items.map(({ key, label }) => (
                          <label
                            key={key}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <Checkbox
                              checked={value.includes(key)}
                              onCheckedChange={() => toggle(key)}
                            />
                            <span className="text-sm">{label}</span>
                          </label>
                        ))}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }} />
            </div>

            <Separator />

            <div className="space-y-3">
              <h3 className="font-semibold text-sm">{t("leagues.form.dates")}</h3>
              {/* UPPDRAG-53: 3 league date fields (registration_deadline /
                  start_date / end_date) swapped from native <Input type="date">
                  to DatePickerWithHolidays. Same string contract (YYYY-MM-DD)
                  so zod schema + save mutations are unchanged. */}
              <div className="grid grid-cols-3 gap-3">
                <FormField control={form.control} name="registration_deadline" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("leagues.form.registrationDeadline")}</FormLabel>
                    <FormControl>
                      <DatePickerWithHolidays value={field.value ?? ""} onChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )} />
                <FormField control={form.control} name="start_date" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("leagues.form.start")}</FormLabel>
                    <FormControl>
                      <DatePickerWithHolidays value={field.value ?? ""} onChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )} />
                <FormField control={form.control} name="end_date" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("leagues.form.end")}</FormLabel>
                    <FormControl>
                      <DatePickerWithHolidays value={field.value ?? ""} onChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )} />
              </div>

              {/* PR-10.1: optional Season FK — admin "puzzles" the season
                  later or creates the league standalone. The Select uses
                  a `__none__` sentinel for "no season" because Radix Select
                  rejects empty-string values; mapped to null at payload time. */}
              <SeasonSelectField form={form} organizationId={organization?.id} />
            </div>

            <Separator />

            <Card className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Settings2 className="h-5 w-5 text-muted-foreground" />
                <div>
                  <h3 className="font-semibold text-sm">{t("leagues.form.leagueSettings")}</h3>
                  <p className="text-xs text-muted-foreground">
                    {hasMatchOverrides ? t("leagues.form.usingCustomSettings") : t("leagues.form.usingDefaultSettings")}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {/* UPPDRAG-37: deep-link to the Seriespel section (id="leagues" in AdminSettings.tsx).
                    Same `?tab=` pattern as UPPDRAG-30 (Domäner deep-link); AdminSettings consumes
                    on mount and clears the param so subsequent sidebar clicks don't fight stale URL state. */}
                <Button type="button" variant="outline" size="sm" onClick={() => navigate('/manager/settings?tab=leagues')}>
                  {t("leagues.form.viewSettings")} &rarr;
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowOverrides(!showOverrides)}>
                  {showOverrides ? t("leagues.form.hide") : t("leagues.form.customizeForRound")}
                </Button>
                {hasMatchOverrides && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => {
                    form.setValue("match_duration_minutes", 90);
                    form.setValue("sets", 3);
                    form.setValue("tiebreak_at", "6-6");
                    form.setValue("point_type", "golden_point");
                    form.setValue("play_all_sets", true);
                    form.setValue("time_limit_applies", true);
                    form.setValue("cost_per_player", 0);
                    form.setValue("members_play_free", true);
                    form.setValue("rules_text", "");
                  }}>
                    {t("leagues.form.resetToDefault")}
                  </Button>
                )}
              </div>
              {showOverrides && (
                <div className="border-t pt-4 space-y-4">
                  <div className="space-y-3">
                    <h4 className="font-medium text-xs text-muted-foreground uppercase tracking-wide">{t("leagues.form.economy")}</h4>
                    {customerCategories.length > 0 ? (
                      <div className="space-y-3">
                        <p className="text-xs text-muted-foreground">{t("leagues.form.categoryPriceHint")}</p>
                        <div className="space-y-2">
                          {customerCategories.map((cat: any) => (
                            <div key={cat.id} className="flex items-center gap-3">
                              <span className="text-sm w-32 truncate">{cat.name}</span>
                              <NumberInput
                                className="w-32"
                                placeholder="0"
                                value={categoryPrices[cat.id] ?? ""}
                                onValueChange={(v) => setCategoryPrices((prev) => ({
                                  ...prev,
                                  [cat.id]: v,
                                }))}
                              />
                              <span className="text-xs text-muted-foreground">kr</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                        <FormField control={form.control} name="cost_per_player" render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("leagues.form.pricePerPlayer")}</FormLabel>
                            <FormControl><NumberInput value={field.value} onValueChange={(v) => field.onChange(v)} onBlur={field.onBlur} name={field.name} /></FormControl>
                          </FormItem>
                        )} />
                        {/* UPPDRAG-38: members_play_free kept for backward-compat
                            (still read by LeagueInfoCard's existing badge).
                            Per-tier configuration lives in the
                            LeagueMembershipPricing component below. A future
                            UPPDRAG will drop this column once all callers move
                            to league_membership_pricing. */}
                        <FormField control={form.control} name="members_play_free" render={({ field }) => (
                          <FormItem className="flex items-center gap-3 pt-6">
                            <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                            <FormLabel className="!mt-0">{t("leagues.form.freeForMembers")}</FormLabel>
                          </FormItem>
                        )} />
                      </div>
                    )}

                    {/* UPPDRAG-38: per-membership-tier pricing matrix. Mounts only
                        when league.id + organization_id are known (the table FKs
                        leagues + membership_plans). For new draft leagues the
                        organization comes from the user's membership record; the
                        existing draft auto-create flow ensures league.id is set
                        by the time this renders. */}
                    {league?.id && league?.organization_id && (
                      <div className="border-t pt-3 mt-3">
                        <LeagueMembershipPricing
                          leagueId={league.id}
                          organizationId={league.organization_id}
                          basePriceKr={Number(form.watch("cost_per_player") ?? 0)}
                        />
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* PR-12.3: roster size range. Default 2/2 mirrors padel-doubles;
                      admins can widen to e.g. 3/6 for squad leagues with subs.
                      TASK-67 PR-D: split into 4 inputs — min/max playing range
                      stays for backwards compat, plus the new per-role caps
                      (max_primary_per_team / max_reserves_per_team) that
                      drive the AdminTeams CreateTeamDialog multi-selects. */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-xs text-muted-foreground uppercase tracking-wide">
                      {t("leagues.form.rosterSize", "Lagstorlek")}
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField control={form.control} name="min_players_per_team" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("leagues.form.minPlayers", "Minst antal spelare")}</FormLabel>
                          <FormControl><NumberInput value={field.value} onValueChange={(v) => field.onChange(v)} onBlur={field.onBlur} name={field.name} /></FormControl>
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="max_players_per_team" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("leagues.form.maxPlayers", "Högst antal spelare")}</FormLabel>
                          <FormControl><NumberInput value={field.value} onValueChange={(v) => field.onChange(v)} onBlur={field.onBlur} name={field.name} /></FormControl>
                        </FormItem>
                      )} />
                    </div>
                    {/* TASK-67 PR-D: per-role caps. Persist via saveMutation
                        as max_primary_per_team / max_reserves_per_team
                        (PR-A added the columns). Defaults are 2/2; admins
                        can raise to e.g. 8/4 for squad leagues. */}
                    <div className="grid grid-cols-2 gap-3">
                      <FormField control={form.control} name="max_primary_per_team" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("leagues.form.maxPrimaryPerTeam", "Max ordinarie spelare per lag")}</FormLabel>
                          <FormControl><NumberInput value={field.value} onValueChange={(v) => field.onChange(v)} onBlur={field.onBlur} name={field.name} /></FormControl>
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="max_reserves_per_team" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("leagues.form.maxReservesPerTeam", "Max reserver per lag")}</FormLabel>
                          <FormControl><NumberInput value={field.value} onValueChange={(v) => field.onChange(v)} onBlur={field.onBlur} name={field.name} /></FormControl>
                        </FormItem>
                      )} />
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <h4 className="font-medium text-xs text-muted-foreground uppercase tracking-wide">{t("leagues.form.matchFormat")}</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField control={form.control} name="match_duration_minutes" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("leagues.form.matchDuration")}</FormLabel>
                          <Select onValueChange={(v) => field.onChange(Number(v))} defaultValue={String(field.value)}>
                            <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                            <SelectContent>
                              <SelectItem value="60">60</SelectItem>
                              <SelectItem value="90">90</SelectItem>
                              <SelectItem value="120">120</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="sets" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("leagues.form.numberOfSets")}</FormLabel>
                          <FormControl><NumberInput value={field.value} onValueChange={(v) => field.onChange(v)} onBlur={field.onBlur} name={field.name} /></FormControl>
                        </FormItem>
                      )} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField control={form.control} name="tiebreak_at" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("leagues.form.tiebreakAt")}</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                            <SelectContent>
                              <SelectItem value="6-6">6-6</SelectItem>
                              <SelectItem value="4-4">4-4</SelectItem>
                              <SelectItem value="none">{t("leagues.form.noTiebreak")}</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="point_type" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("leagues.form.pointType")}</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                            <SelectContent>
                              <SelectItem value="golden_point">Golden point</SelectItem>
                              <SelectItem value="starpoint">Starpoint</SelectItem>
                              <SelectItem value="deuce">{t("leagues.form.regularDeuce")}</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )} />
                    </div>
                    <div className="flex flex-wrap gap-6">
                      <FormField control={form.control} name="play_all_sets" render={({ field }) => (
                        <FormItem className="flex items-center gap-2">
                          <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                          <FormLabel className="!mt-0">{t("leagues.form.playAllSets")}</FormLabel>
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="time_limit_applies" render={({ field }) => (
                        <FormItem className="flex items-center gap-2">
                          <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                          <FormLabel className="!mt-0">{t("leagues.form.timeLimit")}</FormLabel>
                        </FormItem>
                      )} />
                    </div>
                  </div>

                  <Separator />

                  {/* UPPDRAG-40: rich-text rules — bold/italic/headings/lists/quotes.
                      Output is HTML stored in the existing TEXT column; the
                      player-side LeagueInfoCard renders via <RichTextDisplay>
                      which sanitizes through DOMPurify. */}
                  <FormField control={form.control} name="rules_text" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("leagues.form.rules")} (svenska)</FormLabel>
                      <FormControl>
                        <RichTextEditor
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          minHeightPx={160}
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  {/* UPPDRAG-40: English rules. Schema column already exists
                      (leagues.rules_text_en); previously only editable via
                      SettingsLeagues. Surfaces here so admins can localize
                      alongside the Swedish version. Empty preserved as NULL. */}
                  <FormField control={form.control} name="rules_text_en" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("leagues.form.rules")} (English)</FormLabel>
                      <FormControl>
                        <RichTextEditor
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          minHeightPx={120}
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                </div>
              )}
            </Card>

            {/* PR-16.1: Sponsorer section. Two variants — Mina serier
                (player has registered) + Anmäl dig (player browsing the
                signup tab). PR-16.3 replaced the plain logo URL inputs
                with <SponsorLogoUpload> (file picker + URL fallback);
                click URL stays as plain Input. */}
            <SponsorsSection form={form} leagueId={league?.id} />

            <Button type="submit" className="w-full" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? t("leagues.form.saving") : t("leagues.form.save")}
            </Button>
          </form>
        </Form>
      </TabsContent>

      {/* Step 2: Divisions & Teams */}
      {/* PR-10.1: Omgångar tab — chained rounds within season */}
      <TabsContent value="rounds">
        {league?.id && <LeagueRoundsManager leagueId={league.id} />}
      </TabsContent>

      <TabsContent value="divisions">
        {hasId && (
          <div className="space-y-6">
            <Card className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Settings2 className="h-5 w-5 text-muted-foreground" />
                <div>
                  <h3 className="font-semibold text-sm">{t("leagues.form.scoringAndDivisions")}</h3>
                  <p className="text-xs text-muted-foreground">
                    {hasScoringOverrides ? t("leagues.form.usingCustomSettings") : t("leagues.form.usingDefaultSettings")}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {/* UPPDRAG-37: deep-link to the Seriespel section (id="leagues" in AdminSettings.tsx).
                    Same `?tab=` pattern as UPPDRAG-30 (Domäner deep-link). */}
                <Button type="button" variant="outline" size="sm" onClick={() => navigate('/manager/settings?tab=leagues')}>
                  {t("leagues.form.viewSettings")} &rarr;
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowScoringOverrides(!showScoringOverrides)}>
                  {showScoringOverrides ? t("leagues.form.hide") : t("leagues.form.customizeForRound")}
                </Button>
                {hasScoringOverrides && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => {
                    scoringForm.setValue("set_win", 3);
                    scoringForm.setValue("set_loss", 0);
                    scoringForm.setValue("set_draw", 1);
                    scoringForm.setValue("walkover_winner", 9);
                    scoringForm.setValue("walkover_loser", 0);
                    scoringForm.setValue("walkover_no_notice_penalty", -2);
                    scoringForm.setValue("max_wo_before_block", 2);
                    scoringForm.setValue("max_teams_per_division", 8);
                    scoringForm.setValue("promotion_count", 1);
                    scoringForm.setValue("relegation_count", 1);
                    // UPPDRAG-39: reset incomplete-set leader-rule to defaults.
                    scoringForm.setValue("incomplete_set_lead_threshold", 2);
                    scoringForm.setValue("incomplete_set_share_winner", 1);
                    scoringForm.setValue("incomplete_set_share_loser", 1);
                    // UPPDRAG-50: reset table-sort principle to default.
                    scoringForm.setValue("table_sort_method", "standard");
                    // UPPDRAG-51: reset ball-holder principle to default.
                    scoringForm.setValue("ball_holder_principle", "alternating_pair");
                    // UPPDRAG-56: reset reminders to disabled (NULL).
                    scoringForm.setValue("reminder_hours_before", null);
                  }}>
                    {t("leagues.form.resetToDefault")}
                  </Button>
                )}
              </div>
              {showScoringOverrides && (
                <div className="border-t pt-4">
                  <Form {...scoringForm}>
                    <form onSubmit={scoringForm.handleSubmit((v) => saveScoringMutation.mutate(v))} className="space-y-4">
                      <h4 className="font-medium text-xs text-muted-foreground uppercase tracking-wide">{t("leagues.form.scoringSystem")}</h4>
                      <div className="grid grid-cols-3 gap-3">
                        <FormField control={scoringForm.control} name="set_win" render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("leagues.form.setWin")}</FormLabel>
                            <FormControl><NumberInput value={field.value} onValueChange={(v) => field.onChange(v)} onBlur={field.onBlur} name={field.name} /></FormControl>
                          </FormItem>
                        )} />
                        <FormField control={scoringForm.control} name="set_loss" render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("leagues.form.setLoss")}</FormLabel>
                            <FormControl><NumberInput value={field.value} onValueChange={(v) => field.onChange(v)} onBlur={field.onBlur} name={field.name} /></FormControl>
                          </FormItem>
                        )} />
                        <FormField control={scoringForm.control} name="set_draw" render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("leagues.form.draw")}</FormLabel>
                            <FormControl><NumberInput value={field.value} onValueChange={(v) => field.onChange(v)} onBlur={field.onBlur} name={field.name} /></FormControl>
                          </FormItem>
                        )} />
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <FormField control={scoringForm.control} name="walkover_winner" render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("leagues.form.woWinner")}</FormLabel>
                            <FormControl><NumberInput value={field.value} onValueChange={(v) => field.onChange(v)} onBlur={field.onBlur} name={field.name} /></FormControl>
                          </FormItem>
                        )} />
                        <FormField control={scoringForm.control} name="walkover_loser" render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("leagues.form.woLoser")}</FormLabel>
                            <FormControl><NumberInput value={field.value} onValueChange={(v) => field.onChange(v)} onBlur={field.onBlur} name={field.name} /></FormControl>
                          </FormItem>
                        )} />
                        <FormField control={scoringForm.control} name="walkover_no_notice_penalty" render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("leagues.form.woNoNotice")}</FormLabel>
                            <FormControl><NumberInput value={field.value} onValueChange={(v) => field.onChange(v)} onBlur={field.onBlur} name={field.name} /></FormControl>
                          </FormItem>
                        )} />
                      </div>

                      <Separator />

                      {/* UPPDRAG-39: configurable incomplete-set leader-rule.
                          Threshold = game lead at time-out for the set to count
                          as a SET WIN (uses set_win/set_loss above). Below
                          threshold, slight-leader gets share_winner and
                          slight-trailer gets share_loser. Defaults 2/1/1
                          match real padel scoring. */}
                      <h4 className="font-medium text-xs text-muted-foreground uppercase tracking-wide">
                        Oavslutat set (tidsbegränsad match)
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        Vid tidsutgång: lag som leder med minst tröskelvärdet räknas som setvinst (använder Setvinst/Setförlust ovan). Annars delas poängen mellan slight-ledare och slight-förlorare.
                      </p>
                      <div className="grid grid-cols-3 gap-3">
                        <FormField control={scoringForm.control} name="incomplete_set_lead_threshold" render={({ field }) => (
                          <FormItem>
                            <FormLabel>Tröskel (game-ledning)</FormLabel>
                            <FormControl><NumberInput value={field.value} onValueChange={(v) => field.onChange(v)} onBlur={field.onBlur} name={field.name} min={1} max={7} /></FormControl>
                            <p className="text-[11px] text-muted-foreground">Default: 2</p>
                          </FormItem>
                        )} />
                        <FormField control={scoringForm.control} name="incomplete_set_share_winner" render={({ field }) => (
                          <FormItem>
                            <FormLabel>Slight-ledare poäng</FormLabel>
                            <FormControl><NumberInput value={field.value} onValueChange={(v) => field.onChange(v)} onBlur={field.onBlur} name={field.name} min={0} max={10} /></FormControl>
                            <p className="text-[11px] text-muted-foreground">Default: 1</p>
                          </FormItem>
                        )} />
                        <FormField control={scoringForm.control} name="incomplete_set_share_loser" render={({ field }) => (
                          <FormItem>
                            <FormLabel>Slight-förlorare poäng</FormLabel>
                            <FormControl><NumberInput value={field.value} onValueChange={(v) => field.onChange(v)} onBlur={field.onBlur} name={field.name} min={0} max={10} /></FormControl>
                            <p className="text-[11px] text-muted-foreground">Default: 1</p>
                          </FormItem>
                        )} />
                      </div>

                      <Separator />

                      <h4 className="font-medium text-xs text-muted-foreground uppercase tracking-wide">{t("leagues.form.woHandling")}</h4>
                      <FormField control={scoringForm.control} name="max_wo_before_block" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("leagues.form.maxWoBeforeBlock")}</FormLabel>
                          <FormControl><NumberInput value={field.value} onValueChange={(v) => field.onChange(v)} onBlur={field.onBlur} name={field.name} /></FormControl>
                          <p className="text-xs text-muted-foreground">{t("leagues.form.maxWoHint")}</p>
                        </FormItem>
                      )} />

                      <Separator />

                      <h4 className="font-medium text-xs text-muted-foreground uppercase tracking-wide">{t("leagues.form.divisionSettings")}</h4>
                      <div className="grid grid-cols-3 gap-3">
                        <FormField control={scoringForm.control} name="max_teams_per_division" render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("leagues.form.maxTeamsPerDivision")}</FormLabel>
                            <FormControl><NumberInput value={field.value} onValueChange={(v) => field.onChange(v)} onBlur={field.onBlur} name={field.name} /></FormControl>
                          </FormItem>
                        )} />
                        <FormField control={scoringForm.control} name="promotion_count" render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("leagues.form.promotion")}</FormLabel>
                            <FormControl><NumberInput value={field.value} onValueChange={(v) => field.onChange(v)} onBlur={field.onBlur} name={field.name} /></FormControl>
                          </FormItem>
                        )} />
                        <FormField control={scoringForm.control} name="relegation_count" render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("leagues.form.relegation")}</FormLabel>
                            <FormControl><NumberInput value={field.value} onValueChange={(v) => field.onChange(v)} onBlur={field.onBlur} name={field.name} /></FormControl>
                          </FormItem>
                        )} />
                      </div>

                      {/* UPPDRAG-50: tabellsorterings-princip. Schema + UI now;
                          actual sort logic lands in UPPDRAG-50-followup once
                          the standings calc pipeline is built (publish-time
                          RPC writing standings rows from league_matches).
                          Until then this Select is INERT — value persists
                          but StandingsTable still shows the empty-state. */}
                      <FormField control={scoringForm.control} name="table_sort_method" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tabellsortering</FormLabel>
                          <FormControl>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="max-w-[480px]">
                                <SelectItem value="standard">
                                  <div className="flex flex-col">
                                    <span className="font-medium">Standard</span>
                                    <span className="text-[11px] text-muted-foreground">Poäng → gameskillnad → inbördes möte → antal vinster</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="head_to_head">
                                  <div className="flex flex-col">
                                    <span className="font-medium">Inbördes prio</span>
                                    <span className="text-[11px] text-muted-foreground">Poäng → inbördes möte → gameskillnad → antal vinster</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="game_diff">
                                  <div className="flex flex-col">
                                    <span className="font-medium">Game-prio</span>
                                    <span className="text-[11px] text-muted-foreground">Poäng → gameskillnad → setskillnad → inbördes möte</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="set_diff">
                                  <div className="flex flex-col">
                                    <span className="font-medium">Set-prio</span>
                                    <span className="text-[11px] text-muted-foreground">Poäng → setskillnad → gameskillnad → inbördes möte</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="win_percentage">
                                  <div className="flex flex-col">
                                    <span className="font-medium">Vinstprocent</span>
                                    <span className="text-[11px] text-muted-foreground">Vinstprocent → poäng → gameskillnad</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="wo_adjusted">
                                  <div className="flex flex-col">
                                    <span className="font-medium">WO-justerad</span>
                                    <span className="text-[11px] text-muted-foreground">Poäng (utan WO) → ordinarie poäng → gameskillnad</span>
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <p className="text-[11px] text-muted-foreground">
                            Aktiveras när serien har genererat matcher och beräknat tabellen (kommer i nästa version).
                          </p>
                        </FormItem>
                      )} />

                      {/* UPPDRAG-51: Bollhållare-princip. Read by AdminLeagues
                          publishMutation when generating league_matches —
                          picks which player brings balls to each match per
                          the chosen rule. Algorithm covered by 25 vitest
                          cases in src/test/lib/ballHolder.test.ts. */}
                      <FormField control={scoringForm.control} name="ball_holder_principle" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Bollhållare</FormLabel>
                          <FormControl>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="max-w-[480px]">
                                <SelectItem value="alternating_pair">
                                  <div className="flex flex-col">
                                    <span className="font-medium">Varannan gång mellan två spelare</span>
                                    <span className="text-[11px] text-muted-foreground">Hemmalagets två spelare alternerar matcherna inom omgången</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="by_match_number">
                                  <div className="flex flex-col">
                                    <span className="font-medium">Enligt spelnummer</span>
                                    <span className="text-[11px] text-muted-foreground">Fördelas jämnt över alla 4 spelare per omgång (match-index modulo 4)</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="club_provides">
                                  <div className="flex flex-col">
                                    <span className="font-medium">Bollar tillhandahålls av klubben</span>
                                    <span className="text-[11px] text-muted-foreground">Ingen spelartilldelning — klubben tar med bollarna</span>
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <p className="text-[11px] text-muted-foreground">
                            Bollhållare beräknas när serien publiceras. Befintliga matcher uppdateras inte automatiskt vid principbyte.
                          </p>
                        </FormItem>
                      )} />

                      {/* UPPDRAG-56: Påminnelser. Toggle gates the
                          NumberInput. NULL persisted in DB = disabled.
                          Cron 'send-league-match-reminders' runs every
                          30 min and picks up matches that fall within
                          the configured window with reminder_sent_at
                          IS NULL — fully idempotent. */}
                      <FormField control={scoringForm.control} name="reminder_hours_before" render={({ field }) => {
                        const enabled = field.value !== null;
                        return (
                          <FormItem className="rounded-md border bg-muted/20 p-3 space-y-2">
                            <div className="flex items-center justify-between">
                              <FormLabel className="text-sm font-medium">Påminnelsemail</FormLabel>
                              <Switch
                                checked={enabled}
                                onCheckedChange={(v) => field.onChange(v ? 72 : null)}
                              />
                            </div>
                            {enabled ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">Skicka</span>
                                <NumberInput
                                  value={field.value ?? 72}
                                  onValueChange={(v) => field.onChange(v ? Math.max(1, Math.min(720, Math.round(v))) : null)}
                                  onBlur={field.onBlur}
                                  name={field.name}
                                  min={1}
                                  max={720}
                                />
                                <span className="text-xs text-muted-foreground">timmar innan match</span>
                              </div>
                            ) : null}
                            <p className="text-[11px] text-muted-foreground">
                              Spelare får ett email med matchdetaljer, motståndare och bollhållare. Påminnelser dispatchas var 30:e minut av en bakgrundsjobb. MATCHi-importerade spelare med platshållare-mailadresser hoppas över.
                            </p>
                          </FormItem>
                        );
                      }} />

                      <Button type="submit" size="sm" disabled={saveScoringMutation.isPending}>
                        {saveScoringMutation.isPending ? t("leagues.form.saving") : t("leagues.form.saveRules")}
                      </Button>
                    </form>
                  </Form>
                </div>
              )}
            </Card>

            <Separator />

            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm">{t("leagues.form.divisionsAndTeams")}</h3>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setImportOpen(true)}>
                  <Upload className="h-3.5 w-3.5" /> {t("leagues.form.importCsv")}
                </Button>
                <LeagueCSVExport leagueId={league.id} leagueName={league.name} />
              </div>
            </div>
            <LeagueCSVImport open={importOpen} onOpenChange={setImportOpen} leagueId={league.id} organizationId={organization!.id} />
            <AdminDivisions
              leagueId={league.id}
              maxTeamsPerDivision={league.max_teams_per_division ?? 8}
              promotionCount={league.promotion_count ?? 2}
              relegationCount={league.relegation_count ?? 2}
              /* UPPDRAG-46: pass current league-wide default so the
                 per-division Matchtid Select can label the inherit
                 option as "Använd seriens inställning (X min)". */
              leagueMatchDurationMinutes={league.match_duration_minutes ?? 90}
            />
          </div>
        )}
      </TabsContent>

      {/* Step 3: Division times */}
      <TabsContent value="times">
        {hasId && (
          <LeagueDivisionTimes
            leagueId={league.id}
            courts={courts}
            matchDuration={league?.match_duration_minutes ?? 90}
            leagueStartDate={league?.start_date ?? undefined}
          />
        )}
      </TabsContent>

      {/* Step 4: Schedule */}
      <TabsContent value="schedule">
        {hasId && (
          <div className="space-y-4">
            {/* Q9 cleanup (refactor/q9-delete-tab4-generera-spelschema):
                Tab 4's whole-league "Generera spelschema" button removed.
                The canonical replacement is the per-omgång "Generera
                matcher" Sheet on the Omgångar tab (PR-B / TASK-73 Phase 1
                — see src/components/admin/LeagueRoundsManager.tsx). The
                old button was round-unaware (used league dates instead
                of round dates) and produced confusing "Kunde inte
                placera några matcher" toasts; per audit §05 Q9. */}
            {hallHours && (
              <LeagueCalendar
                leagueId={league.id}
                courts={courts}
                hallHours={hallHours}
                matchDuration={league?.match_duration_minutes ?? 90}
                initialDate={league?.start_date ?? undefined}
                enableZoneMode
              />
            )}

            <Collapsible>
              <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground">
                <ChevronDown className="h-3 w-3" /> {t("leagues.form.manualExtraTimes")}
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2">
                <LeagueExtraSlots
                  leagueId={league.id}
                  matchDurationMinutes={league?.match_duration_minutes ?? 90}
                  autoReleaseHours={league?.auto_release_hours ?? 24}
                />
              </CollapsibleContent>
            </Collapsible>

            <div className="flex items-center gap-2 text-xs text-muted-foreground border-t pt-3">
              <Settings2 className="h-3.5 w-3.5" />
              <button onClick={() => navigate('/manager/settings')} className="hover:underline text-primary">
                {t("leagues.form.rescheduleRulesHint")}
              </button>
            </div>
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}

// ─── SeasonSelectField (PR-10.1) ───────────────────────────────────────
// Loads org-scoped seasons and renders a Select with a "Ingen säsong"
// sentinel option. Mapped to null in saveMutation's payload.

function SeasonSelectField({
  form,
  organizationId,
}: {
  form: ReturnType<typeof useForm<LeagueFormValues>>;
  organizationId: string | undefined;
}) {
  const { t } = useTranslation("admin");
  const { data: seasons = [] } = useQuery({
    queryKey: ["leagueform-seasons", organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("seasons")
        .select("id, name, status, start_date, end_date")
        .eq("organization_id", organizationId!)
        .order("start_date", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!organizationId,
  });

  return (
    <FormField
      control={form.control}
      name="season_id"
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {t("leagues.form.season", "Säsong")}{" "}
            <span className="text-xs text-muted-foreground font-normal">
              {t("leagues.form.seasonOptional", "(valfritt)")}
            </span>
          </FormLabel>
          <FormControl>
            <Select
              value={field.value ?? "__none__"}
              onValueChange={(v) => field.onChange(v === "__none__" ? null : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("leagues.form.seasonPlaceholder", "Välj säsong (valfritt)")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">
                  {t("leagues.form.seasonNone", "Ingen säsong")}
                </SelectItem>
                {seasons.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                    {s.status === "draft" ? ` (${t("leagues.form.seasonStatusDraft", "utkast")})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormControl>
        </FormItem>
      )}
    />
  );
}

// ─── SponsorsSection (PR-16.1, file-upload added in PR-16.3) ───────────
// Collapsible Card with sponsor management for two variants:
//   - my_series (player "Mina serier" card)
//   - register  (player "Anmäl dig" card)
// Each variant has:
//   - <SponsorLogoUpload> (PR-16.3): file picker + URL fallback +
//     "Ta bort" button. Persists into the same form field as before.
//   - Plain Input for the click-through URL (Q5: clickable to sponsor URL).
// Disabled when the league hasn't been saved yet (no league.id) — admin
// must save first before uploading. Mirrors the LeagueRoundsManager
// hasId gate from PR-10.1.

function SponsorsSection({
  form,
  leagueId,
}: {
  form: ReturnType<typeof useForm<LeagueFormValues>>;
  leagueId: string | undefined;
}) {
  const { t } = useTranslation("admin");

  return (
    <Card className="p-4 space-y-3">
      <Collapsible defaultOpen={false}>
        <CollapsibleTrigger className="flex w-full items-center gap-2 hover:opacity-80">
          <ChevronDown className="h-4 w-4 transition-transform data-[state=open]:rotate-180" />
          <div className="text-left">
            <h3 className="font-semibold text-sm">
              {t("leagues.form.sponsorsTitle", "Sponsorer")}{" "}
              <span className="text-xs text-muted-foreground font-normal">
                {t("leagues.form.sponsorsOptional", "(valfritt)")}
              </span>
            </h3>
            <p className="text-xs text-muted-foreground">
              {t(
                "leagues.form.sponsorsHint",
                "Visa sponsorlogga på spelarvyerna. Två varianter — på 'Mina serier' (anmälda spelare) och 'Anmäl dig' (öppna serier).",
              )}
            </p>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-3">
          {/* Variant 1: Mina serier (registered players' card) */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              {t("leagues.form.sponsorMySeriesTitle", "Mina serier — sponsorlogga")}
            </Label>
            <FormField
              control={form.control}
              name="sponsor_my_series_logo_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">
                    {t("leagues.form.sponsorLogoUrl", "Logga")}
                  </FormLabel>
                  <FormControl>
                    {/* PR-16.3: file picker + URL fallback */}
                    <SponsorLogoUpload
                      leagueId={leagueId}
                      variant="my_series"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="sponsor_my_series_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">
                    {t("leagues.form.sponsorClickUrl", "Länk vid klick")}
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="https://sponsor.example.com" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <Separator />

          {/* Variant 2: Anmäl dig (open registration card) */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">
              {t("leagues.form.sponsorRegisterTitle", "Anmäl dig — sponsorlogga")}
            </Label>
            <FormField
              control={form.control}
              name="sponsor_register_logo_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">
                    {t("leagues.form.sponsorLogoUrl", "Logga")}
                  </FormLabel>
                  <FormControl>
                    <SponsorLogoUpload
                      leagueId={leagueId}
                      variant="register"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="sponsor_register_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">
                    {t("leagues.form.sponsorClickUrl", "Länk vid klick")}
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="https://sponsor.example.com" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

// PR-16.1's inline SponsorThumb has been folded into the new
// <SponsorLogoUpload> component (PR-16.3). The thumbnail render lives
// there now alongside the file picker, URL fallback, and "Ta bort"
// button — keeping the logo UX in one place per variant.
